import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MessagesSquare } from 'lucide-react';
import PageHead from '../../components/PageHead/PageHead';
import SubTabs from '../../components/SubTabs/SubTabs';
import DirectMessaging from '../DirectMessaging/DirectMessaging';
import Feedback from '../Feedback/Feedback';
import { useI18n } from '../../i18n/I18nContext';
import './InboxHub.css';

/**
 * Inbox hub — single "inbound communication" destination. Merges:
 *   • Chats — 1:1 conversations with students (a full-height split card)
 *   • Feedback — course reviews & ratings (a scrolling page)
 * The chats tab is a "fill" page (the shell gives it the whole column and no bottom gutter);
 * the reviews tab scrolls with the page. (Redesign 2026, Phase 5.)
 */

function InboxHub() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const active = params.get('tab') === 'feedback' ? 'feedback' : 'messages';
  // An open conversation: a phone gives it the whole screen, so the header and the tabs step aside.
  const [threadOpen, setThreadOpen] = useState(false);
  // How many conversations have something unread: the chats report it, the tab wears it.
  const [unreadChats, setUnreadChats] = useState(0);

  const TABS = [
    { key: 'messages', label: t('messaging.tabChats'), count: unreadChats > 0 ? unreadChats : undefined },
    { key: 'feedback', label: t('feedback.title') },
  ];

  const setTab = (key) => {
    const next = new URLSearchParams(params);
    if (key === 'messages') next.delete('tab');
    else next.set('tab', key);
    setParams(next, { replace: true });
  };

  const chats = active === 'messages';

  return (
    <div
      className={`inbox-hub${chats && threadOpen ? ' is-thread-open' : ''}`}
      data-page={chats ? 'fill' : undefined}
    >
      <PageHead icon={<MessagesSquare />} title={t('nav.inbox')} description={t('messaging.hubSubtitle')} />

      <SubTabs className="inbox-hub-tabs" tabs={TABS} active={active} onChange={setTab} ariaLabel={t('nav.inbox')} />

      <div className="inbox-hub-body">
        {chats
          ? <DirectMessaging onThreadOpenChange={setThreadOpen} onUnreadChange={setUnreadChats} />
          : <Feedback embedded />}
      </div>
    </div>
  );
}

export default InboxHub;
